import { AudioTrackInfo, MixerTrackLabel } from '@/modules/audiomultitrack/types'
import store from '@/store'
import { Optional } from '@/types'
import { InvitedCaster } from '@/types/casts'
import { UserProfile } from '@/types/users'
import { Cast } from '../classes'
import studio, { CollectionNames } from '@/modules/database/utils'
import { Result } from '@/modules/errorHandling/results'
import { DatabaseAction, MergeOption, WithFieldActions } from '@/modules/database/utils/database'
import { hasAudioTracksMixin } from '@/modules/classBuilder/mixins/hasAudioTracks'

export const confirmRemoveCasterFromCast = async (userId: string, cast: Readonly<Cast|null>,
                                                  profile: Optional<Readonly<UserProfile>>) => {
  if (cast === null) return
  try {
    // TODO: Use version from casts module instead of events. (But it requires the cast to be part of filteredCasts.)
    await store.dispatch.events.removeCasterFromCast({
      casterId: userId, castId: cast.id, anonymousId: profile?.anonymousId ?? undefined
    })
    store.dispatch.logs.addLog({
      message: 'User removed as caster from cast.',
      cast: cast.id,
      level: 'info'
    }).catch((error) => console.error('Failed to log removeCasterFromCast success', error))
    return true
  } catch (error) {
    store.dispatch.logs.addLog({
      message: 'Unable to remove caster from cast.',
      cast: cast?.id,
      error: error,
      level: 'info'
    }).catch((error) => console.error('Failed to log removeCasterFromCast failure', error))
    return false
  }
}

export const moveCasterToAudioTrack = async (userId: string, cast: Cast, audioTrack: MixerTrackLabel) => {
  if (!hasAudioTracksMixin(cast)) return Result.fail('invalid_data', 'Can only move caster in multitrack cast')
  const currentInvitedCaster = cast.invited_casters[userId]
  if (!currentInvitedCaster) return Result.fail('invalid_data', 'Cannot move caster that was not an invited caster')
  const audioTracks:  WithFieldActions<Partial<Record<MixerTrackLabel, AudioTrackInfo>>> = {}
  for (const trackLabel of Object.keys(currentInvitedCaster.audioTracks ?? {})) {
    audioTracks[trackLabel as MixerTrackLabel] = DatabaseAction.deleteField()
  }
  audioTracks[audioTrack] = { trackId: 1 }
  const castData = { invited_casters: { [userId]: { audioTracks, deleted: false } } }
  const result = await studio.db.collection(CollectionNames.CASTS).doc(cast.id).set(castData, MergeOption.MERGE)
  if (result.isSuccess) {
    store.dispatch.logs.addLog({
      message: `Caster moved to track ${audioTrack} in cast.`,
      cast: cast.id, data: { user: userId, cast: cast.id }, level: 'info'
    }).catch((error) => console.error(error))
    return Result.ok()
  } else {
    store.dispatch.logs.addLog({
      message: `Failed to move caster ${userId} to track ${audioTrack}`,
      cast: cast.id, data: { error: result.message }, level: 'error'
    }).catch((error) => console.error(error))
    return result
  }
}

export const isValidNonAnonymousInvitedCaster = (caster?: InvitedCaster): boolean => {
  return !!caster && !caster.deleted && !caster.anonymous
}

export const isValidInvitedCaster = (caster?: InvitedCaster): boolean => {
  return !!caster && !caster.deleted
}

export const isValidCasterInCast = (casterId: string|null, cast: Cast): boolean => {
  if (!casterId) return false
  if (cast.invited_casters === null) return false
  return isValidInvitedCaster(cast.invited_casters[casterId])
}
